/**
 * Central registry mapping UI features to backend permission codes.
 *
 * Permission codes follow the `module.action` convention returned by the API
 * (see society_api `config/constants.ts` MODULES / PERMISSION_ACTIONS), e.g.
 * `finance.read`, `meeting.create`. `view` and `read` are treated as aliases
 * by the authStore, so either spelling works everywhere.
 */

export const RBAC_MODULES = [
  'auth',
  'society',
  'role',
  'permission',
  'user',
  'house',
  'resident',
  'meeting',
  'notification',
  'improvement',
  'finance',
  'corpus',
  'collection',
  'expense',
  'reimbursement',
  'document',
  'complaint',
  'event',
  'visitor',
  'asset',
  'report',
  'committee',
  'mobile',
  'election',
  'notice',
  'whatsapp',
  'service',
  'staff',
] as const;

export type RbacModule = (typeof RBAC_MODULES)[number];

export const PERMISSION_ACTIONS = [
  'create',
  'read',
  'update',
  'delete',
  'approve',
  'export',
] as const;

export type PermissionActionName = (typeof PERMISSION_ACTIONS)[number];

export const MODULE_LABELS: Record<string, string> = {
  auth: 'Authentication',
  society: 'Society',
  role: 'Roles',
  permission: 'Permissions',
  user: 'Users',
  house: 'Houses',
  resident: 'Residents',
  meeting: 'Meetings',
  notification: 'Notifications',
  improvement: 'Improvements',
  finance: 'Finance & Banking',
  corpus: 'Corpus Fund',
  collection: 'Collections',
  expense: 'Expenses',
  reimbursement: 'Reimbursements',
  document: 'Documents',
  complaint: 'Complaints',
  event: 'Events',
  visitor: 'Visitors',
  asset: 'Assets',
  report: 'Reports',
  committee: 'Committee',
  mobile: 'Mobile',
  election: 'Elections',
  notice: 'Notices',
  whatsapp: 'WhatsApp',
  service: 'Service Requests',
  staff: 'Society Staff',
  provider: 'Service Providers',
  gatepass: 'Gate Pass',
  service_bill: 'Service Billing',
  service_due: 'Service Dues',
  service_expense: 'Service Expenses',
};

/** Feature → permission code lookup, grouped by module for readability at call sites. */
export const PERMISSIONS = {
  society: {
    read: 'society.read',
    create: 'society.create',
    update: 'society.update',
  },
  role: {
    read: 'role.read',
    update: 'role.update',
  },
  permission: {
    read: 'permission.read',
    create: 'permission.create',
  },
  house: {
    read: 'house.read',
    create: 'house.create',
    update: 'house.update',
    delete: 'house.delete',
  },
  resident: {
    read: 'resident.read',
    create: 'resident.create',
    update: 'resident.update',
    delete: 'resident.delete',
  },
  meeting: {
    read: 'meeting.read',
    create: 'meeting.create',
    update: 'meeting.update',
    approve: 'meeting.approve',
    delete: 'meeting.delete',
  },
  notification: {
    read: 'notification.read',
  },
  finance: {
    read: 'finance.read',
    create: 'finance.create',
    update: 'finance.update',
    delete: 'finance.delete',
    approve: 'finance.approve',
  },
  corpus: {
    read: 'corpus.read',
    create: 'corpus.create',
    update: 'corpus.update',
    approve: 'corpus.approve',
    export: 'corpus.export',
  },
  collection: {
    read: 'collection.read',
    create: 'collection.create',
    update: 'collection.update',
    delete: 'collection.delete',
  },
  expense: {
    read: 'expense.read',
    create: 'expense.create',
    update: 'expense.update',
    approve: 'expense.approve',
    delete: 'expense.delete',
    export: 'expense.export',
  },
  reimbursement: {
    read: 'reimbursement.read',
    create: 'reimbursement.create',
    update: 'reimbursement.update',
    approve: 'reimbursement.approve',
  },
  complaint: {
    read: 'complaint.read',
    create: 'complaint.create',
    update: 'complaint.update',
    delete: 'complaint.delete',
  },
  event: {
    read: 'event.read',
    create: 'event.create',
    update: 'event.update',
    delete: 'event.delete',
  },
  election: {
    read: 'election.read',
    create: 'election.create',
    update: 'election.update',
    delete: 'election.delete',
  },
  document: {
    read: 'document.read',
    create: 'document.create',
    update: 'document.update',
    delete: 'document.delete',
    approve: 'document.approve',
    export: 'document.export',
  },
  notice: {
    read: 'notice.read',
    create: 'notice.create',
    update: 'notice.update',
    approve: 'notice.approve',
    delete: 'notice.delete',
  },
  whatsapp: {
    read: 'whatsapp.read',
    create: 'whatsapp.create',
    update: 'whatsapp.update',
  },
  visitor: {
    read: 'visitor.read',
    create: 'visitor.create',
    update: 'visitor.update',
    delete: 'visitor.delete',
  },
  service: {
    read: 'service.read',
    view: 'service.view',
    create: 'service.create',
    update: 'service.update',
    edit: 'service.edit',
    delete: 'service.delete',
    export: 'service.export',
    cancel: 'service.cancel',
    assign: 'service.assign',
    complete: 'service.complete',
    viewAll: 'service.view_all',
  },
  provider: {
    view: 'provider.view',
    create: 'provider.create',
    edit: 'provider.edit',
    activate: 'provider.activate',
    verify: 'provider.verify',
    block: 'provider.block',
  },
  gatepass: {
    view: 'gatepass.view',
    create: 'gatepass.create',
    verify: 'gatepass.verify',
    checkin: 'gatepass.checkin',
    checkout: 'gatepass.checkout',
  },
  serviceBill: {
    view: 'service_bill.view',
    create: 'service_bill.create',
    edit: 'service_bill.edit',
    approve: 'service_bill.approve',
  },
  serviceDue: {
    view: 'service_due.view',
    create: 'service_due.create',
    cancel: 'service_due.cancel',
  },
  serviceExpense: {
    view: 'service_expense.view',
    create: 'service_expense.create',
    approve: 'service_expense.approve',
  },
  staff: {
    read: 'staff.read',
    create: 'staff.create',
    update: 'staff.update',
    delete: 'staff.delete',
  },
} as const;

/** Named feature gates used directly by pages/buttons for clarity at call sites. */
export const FEATURE_PERMISSIONS = {
  bankingAddAccount: PERMISSIONS.finance.create,
  bankingRecordTransaction: PERMISSIONS.finance.create,
  houseCreate: PERMISSIONS.house.create,
  residentCreate: PERMISSIONS.resident.create,
  meetingCreate: PERMISSIONS.meeting.create,
  meetingSchedule: PERMISSIONS.meeting.update,
  meetingComplete: PERMISSIONS.meeting.approve,
  meetingCancel: PERMISSIONS.meeting.update,
  collectionCreate: PERMISSIONS.collection.create,
  collectionRecordPayment: PERMISSIONS.collection.update,
  expenseCreate: PERMISSIONS.expense.create,
  expenseApprove: PERMISSIONS.expense.approve,
  expensePay: PERMISSIONS.expense.update,
  expenseExport: PERMISSIONS.expense.export,
  reimbursementCreate: PERMISSIONS.reimbursement.create,
  noticeCreate: PERMISSIONS.notice.create,
  noticePublish: PERMISSIONS.notice.approve,
  noticeArchive: PERMISSIONS.notice.update,
  societyUpdate: PERMISSIONS.society.update,
  serviceRequestCreate: PERMISSIONS.service.create,
  serviceProviderManage: PERMISSIONS.provider.edit,
  serviceProviderVerify: PERMISSIONS.provider.verify,
  serviceProviderBlock: PERMISSIONS.provider.block,
  serviceBillManage: PERMISSIONS.serviceBill.create,
  serviceBillApprove: PERMISSIONS.serviceBill.approve,
  visitorDesk: PERMISSIONS.visitor.read,
  visitorEntryExit: PERMISSIONS.visitor.update,
  gatePassVerify: PERMISSIONS.gatepass.verify,
  gatePassCheckin: PERMISSIONS.gatepass.checkin,
  gatePassCheckout: PERMISSIONS.gatepass.checkout,
} as const;
